import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Divider,
  Stack,
  Button,
  Skeleton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WeekStacksSidebar from "../components/WeekStacksSidebar";
import { getTeamCLV, getTeamProjection, normalizePlayerName } from "../helpers/playerHelpers";
import { getTeamsTableFromCsv } from "../helpers/chartHelpers";
import { detectTeamStacks } from "../services/stackDetection";
import { TEAM_COLORS } from "../constants";
import { TOURNAMENT_GROUP_LABELS } from "../helpers/tournamentHelpers";
import { useStatusColors } from "../helpers/statusColors";
import { PositionChip, EmptyState, AccentCard, SectionHeader, PlatformChip } from "../components/ui";
import { teamPlatformStats } from "../helpers/platformSplit";
import { getPlatformColor } from "../config/theme";
import { useDraftData } from "../context/DraftDataContext";
import { useAdp } from "../context/AdpContext";
import { useMarketAdp } from "../helpers/useMarketAdp";
import type { AdpPlayerOption, PlayerTrend, SortDirection, TeamDraft } from "../types/draft";

type TeamSortCol = "date" | "draftSlot" | "tournament" | "cumulativeCLV" | "cumulativeProjection";

type TeamWithCLV = TeamDraft & { cumulativeCLV: number; cumulativeProjection: number };

// ── Entry value curves (small multiples) ──────────────────────────
// Cumulative ADP value across each entry's picks, on a shared y-scale so the
// builds compare: a flat +12 reads differently from a late-surging +42.
function EntryValueCurves({ teams, lookupForProvider }: { teams: TeamWithCLV[]; lookupForProvider: (provider?: string) => Map<string, number> }) {
  const sc = useStatusColors();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Group by tournament type; each entry's cumulative CLV curve, then the
  // average curve across the group (aligned by pick index). Sort-independent —
  // this reads the whole portfolio, not a page of the current sort.
  const groups = useMemo(() => {
    // Grouped by PLATFORM first, then tournament. The two platforms run
    // different draft lengths (UD 18 rounds, DK 20) against different boards, so
    // their curves are only comparable in shape — never poolable into one
    // average. Keying the group by provider keeps them apart even if a
    // tournament title were ever to collide across platforms.
    const byGroup = new Map<string, { provider: string; label: string; entries: number[][] }>();
    for (const t of teams) {
      const pts: number[] = [];
      let cum = 0;
      getTeamCLV(t, lookupForProvider(t.provider)).playerCLVs
        .slice()
        .sort((a, b) => Number(a.pickNumber) - Number(b.pickNumber))
        .forEach(p => { cum += typeof p.clv === "number" ? p.clv : 0; pts.push(cum); });
      if (pts.length < 2) continue;
      const provider = t.provider || "Unknown";
      const label = t.tournament || "Draft";
      const key = `${provider}|${label}`;
      let g = byGroup.get(key);
      if (!g) { g = { provider, label, entries: [] }; byGroup.set(key, g); }
      g.entries.push(pts);
    }

    return [...byGroup.values()].map(({ provider, label, entries }) => {
      const maxLen = Math.max(...entries.map(e => e.length));
      // Average aligned by pick index; denominator = entries that reached index i.
      const avg: number[] = [];
      for (let i = 0; i < maxLen; i++) {
        let sum = 0, n = 0;
        for (const e of entries) if (i < e.length) { sum += e[i]; n++; }
        avg.push(n ? sum / n : 0);
      }
      return { provider, label, entries, avg, maxLen, final: avg[avg.length - 1] ?? 0, count: entries.length };
    }).sort((a, b) => b.final - a.final);
  }, [teams, lookupForProvider]);

  if (groups.length === 0) return null;

  const allPts = groups.flatMap(g => g.entries.flat()).concat(0);
  const yMax = Math.max(...allPts), yMin = Math.min(...allPts);
  const span = (yMax - yMin) || 1;
  const W = 160, H = 54, PAD = 6;
  const yOf = (v: number) => PAD + ((yMax - v) * (H - 2 * PAD)) / span;
  const xOf = (i: number, n: number) => PAD + (i * (W - 2 * PAD)) / Math.max(n - 1, 1);
  const pathOf = (pts: number[], n: number) =>
    pts.map((v, i) => `${i ? "L" : "M"}${xOf(i, n).toFixed(1)} ${yOf(v).toFixed(1)}`).join(" ");

  return (
    <AccentCard component="section" aria-label="Value curves by tournament" sx={{ p: 2.5, mb: 2 }}>
      <SectionHeader
        title="Value curves by tournament"
        subtitle="Average cumulative ADP value per tournament (bold), colored by platform; each entry is a faint trace behind it. Shared scale; dashed line = market (0). Each platform is scored on its own board."
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        {groups.map(g => {
          // Sign tone still colors the NUMBER (is this build ahead of market?);
          // the curve itself carries the platform color so two cards side by
          // side are immediately attributable to UD or DK.
          const tone = g.final >= 0 ? sc.good : sc.bad;
          const platformTone = getPlatformColor(g.provider, isDark);
          return (
            <Box key={`${g.provider}|${g.label}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.25 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.25 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, minWidth: 0 }}>
                    <PlatformChip provider={g.provider} size="sm" variant="outlined" />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
                    avg of {g.count} {g.count === 1 ? "entry" : "entries"}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: tone, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                  {g.final >= 0 ? "+" : ""}{g.final.toFixed(1)}
                </Typography>
              </Box>
              <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: "100%", height: "auto", display: "block" }}>
                <line x1={PAD} y1={yOf(0)} x2={W - PAD} y2={yOf(0)} stroke={sc.neutral} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.6} />
                {g.entries.map((e, idx) => (
                  <path key={idx} d={pathOf(e, g.maxLen)} fill="none" stroke={sc.neutral} strokeWidth={1} strokeOpacity={0.28} strokeLinecap="round" strokeLinejoin="round" />
                ))}
                <path d={pathOf(g.avg, g.maxLen)} fill="none" stroke={platformTone} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                <circle cx={xOf(g.avg.length - 1, g.maxLen)} cy={yOf(g.final)} r={2.8} fill={platformTone} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </AccentCard>
  );
}

/** Signed CLV for a KPI tile; an em dash when the platform has nothing to score. */
function fmtSigned(v: number | null): string {
  if (v === null || isNaN(v)) return "\u2014";
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}`;
}

interface KpiTile {
  label: string;
  value: string | number;
  color?: string;
  /** Value for one platform when the strip is split. */
  perPlatform: (p: ReturnType<typeof teamPlatformStats>[number]) => string;
  /** Optional per-platform color (e.g. CLV sign), overriding `color`. */
  tone?: (p: ReturnType<typeof teamPlatformStats>[number]) => string | undefined;
}

const MOBILE_TEAM_LIST_MAX_HEIGHT = "min(62vh, 500px)";
const TEAMS_PAGE_SIZE = 12;

// ── Draft ADP snapshot lookup ─────────────────────────────────────
const DRAFT_ADP_CUTOFF = "2026-04-03";

function buildDraftAdpLookup(
  draftDate: string,
  adpHistory: PlayerTrend[],
): Map<string, number> | null {
  if (!draftDate || !adpHistory.length) return null;
  const dateStr = draftDate.slice(0, 10);
  if (dateStr < DRAFT_ADP_CUTOFF) return null;
  const allDates = [
    ...new Set(adpHistory.flatMap(p => p.history.map(h => h.date))),
  ].sort();
  const closestDate = allDates.filter(d => d <= dateStr).at(-1);
  if (!closestDate) return null;
  const lookup = new Map<string, number>();
  for (const player of adpHistory) {
    const entry = player.history.find(h => h.date === closestDate);
    if (entry) lookup.set(player.name, entry.adp);
  }
  return lookup;
}

// ── Team list row ─────────────────────────────────────────────────
function TeamListRow({ team, maxAbsCLV, maxProjection, sortCol, selected, onClick, adpLoading, archetypeLabel, showPlatform }: {
  team: TeamWithCLV;
  maxAbsCLV: number;
  maxProjection: number;
  sortCol: TeamSortCol;
  /** Only shown when more than one platform is loaded — a single-platform
   *  portfolio would repeat the same chip on every row for no information. */
  showPlatform: boolean;
  selected: boolean;
  onClick: () => void;
  adpLoading: boolean;
  archetypeLabel?: string;
}) {
  const sc = useStatusColors();
  // The list's right-side metric mirrors the active sort, so the ordering always
  // matches the number you see: cumulative projection when sorting by Proj, else CLV.
  const showProjection = sortCol === "cumulativeProjection";
  const clv = typeof team.cumulativeCLV === "number" && !isNaN(team.cumulativeCLV) ? team.cumulativeCLV : 0;
  const proj = typeof team.cumulativeProjection === "number" && !isNaN(team.cumulativeProjection) ? team.cumulativeProjection : 0;
  const isPositive = clv >= 0;
  const clvPct = maxAbsCLV > 0 ? Math.min(Math.abs(clv) / maxAbsCLV, 1) * 100 : 0;
  const projPct = maxProjection > 0 ? Math.min(proj / maxProjection, 1) * 100 : 0;
  const metricPct = showProjection ? projPct : clvPct;
  const metricColor = showProjection ? "text.primary" : (isPositive ? sc.good : sc.bad);
  const metricBar = showProjection ? sc.neutral : (isPositive ? sc.good : sc.bad);
  const metricText = showProjection ? proj.toFixed(1) : `${isPositive ? "+" : ""}${clv.toFixed(1)}`;

  let formattedDate = "";
  if (team.date) {
    const d = new Date(team.date);
    if (!isNaN(d.getTime())) formattedDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  return (
    <Box
      onClick={onClick}
      // The row is the click target for the detail panel, so it needs to be a
      // real control: reachable by keyboard and announced with the platform it
      // belongs to (which is what disambiguates two same-named tournaments).
      role="button"
      tabIndex={0}
      aria-label={`${team.provider ? `${team.provider} ` : ""}${team.tournament || "Draft"}${team.draftSlot ? `, slot ${team.draftSlot}` : ""}`}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2, py: 1,
        cursor: "pointer", minHeight: 56,
        borderLeft: selected ? "3px solid" : "3px solid transparent",
        borderLeftColor: selected ? "primary.main" : "transparent",
        bgcolor: selected ? "action.selected" : "transparent",
        "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
        transition: "background-color 100ms ease",
      }}
    >
      {/* Text area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* A draft belongs to exactly one platform, so the chip is an unambiguous
            attribution for the CLV on the right — which is scored on that
            platform's own board. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {showPlatform && <PlatformChip provider={team.provider || "Unknown"} size="sm" />}
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {team.tournament || "Draft"}
          </Typography>
        </Box>
        {archetypeLabel && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, my: 0.25 }}>
            {archetypeLabel.split(" · ").map(part => (
              <Chip
                key={part}
                label={part}
                size="small"
                sx={{ fontSize: 10, height: 18, bgcolor: "action.selected", color: "text.secondary" }}
              />
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.3 }}>
          {[team.draftSlot ? `Slot ${team.draftSlot}` : null, formattedDate].filter(Boolean).join("  ·  ")}
        </Typography>
      </Box>

      {/* CLV chip + mini bar */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
        {adpLoading
          ? <Skeleton width={50} height={16} />
          : (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: metricColor }}>
              {metricText}
            </Typography>
          )
        }
        <Box sx={{ width: 60, height: 6, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{
            width: `${metricPct}%`, height: "100%", borderRadius: 1,
            bgcolor: metricBar, opacity: 0.8,
          }} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Team list panel (left) ────────────────────────────────────────
function TeamListPanel({ teams, maxAbsCLV, maxProjection, selectedId, onSelect, adpLoading, sortCol, sortDir, onSort, archetypeByDraftId, showPlatform }: {
  teams: TeamWithCLV[];
  maxAbsCLV: number;
  maxProjection: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  adpLoading: boolean;
  sortCol: TeamSortCol;
  sortDir: "asc" | "desc";
  onSort: (col: TeamSortCol) => void;
  archetypeByDraftId: Map<string, string>;
  showPlatform: boolean;
}) {
  // Page through the list. Sorting/filtering happen on the full list upstream
  // (it arrives here already sorted as `teams`), so paging never affects them.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(teams.length / TEAMS_PAGE_SIZE));
  useEffect(() => { setPage(0); }, [teams]);
  const safePage = Math.min(page, pageCount - 1);
  const visibleTeams = teams.slice(safePage * TEAMS_PAGE_SIZE, safePage * TEAMS_PAGE_SIZE + TEAMS_PAGE_SIZE);

  return (
    <Box sx={{
      width: { xs: "100%", md: 380 },
      height: { xs: MOBILE_TEAM_LIST_MAX_HEIGHT, md: "100%" },
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      borderRight: { md: "1px solid" },
      borderRightColor: { md: "divider" },
      minHeight: 0,
    }}>
      {/* Panel header with sort controls */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 1,
        bgcolor: "background.paper",
        borderBottom: "1px solid", borderBottomColor: "divider",
        px: 2, py: 1,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          {teams.length} team{teams.length !== 1 ? "s" : ""}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={sortCol}
          onChange={(_, val) => { onSort(val ?? sortCol); }}>
          {([
            { val: "date", label: "Date" },
            { val: "cumulativeCLV", label: "CLV" },
            { val: "cumulativeProjection", label: "Proj" },
            { val: "draftSlot", label: "Slot" },
          ] as { val: TeamSortCol; label: string }[]).map(({ val, label }) => (
            <ToggleButton key={val} value={val} sx={{ fontSize: 11, px: 1, py: 0.5 }}>
              {label}{sortCol === val ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Scrollable rows */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {teams.length === 0 && (
          <EmptyState dense title="No teams match your filters" hint="Adjust or clear the player/tournament filters above." />
        )}
        {visibleTeams.map((team, i) => (
          <React.Fragment key={team.draftId}>
            {i > 0 && <Divider />}
            <TeamListRow
              team={team}
              maxAbsCLV={maxAbsCLV}
              maxProjection={maxProjection}
              sortCol={sortCol}
              selected={selectedId === team.draftId}
              onClick={() => onSelect(team.draftId)}
              adpLoading={adpLoading}
              archetypeLabel={archetypeByDraftId.get(team.draftId)}
              showPlatform={showPlatform}
            />
          </React.Fragment>
        ))}
      </Box>

      {/* Pager — pinned below the list */}
      {pageCount > 1 && (
        <Box sx={{
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
          px: 1.5, py: 0.5,
          borderTop: "1px solid", borderColor: "divider",
          bgcolor: "background.paper",
        }}>
          <IconButton
            size="small"
            aria-label="Previous page"
            disabled={safePage === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 92, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
            Page {safePage + 1} of {pageCount}
          </Typography>
          <IconButton
            size="small"
            aria-label="Next page"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

// ── Full roster table ─────────────────────────────────────────────
const POS_ORDER = ["QB", "RB", "WR", "TE", "K", "DEF"];

function groupRosterPlayersByPosition(playerCLVs: ReturnType<typeof getTeamCLV>["playerCLVs"]) {
  const groups: Record<string, typeof playerCLVs> = {};
  for (const pos of POS_ORDER) groups[pos] = [];
  const other: typeof playerCLVs = [];

  for (const p of playerCLVs) {
    const pos = (p.position || "").toUpperCase();
    if (POS_ORDER.includes(pos)) groups[pos].push(p);
    else other.push(p);
  }

  const sortByPick = (arr: typeof playerCLVs) =>
    [...arr].sort((a, b) => {
      const an = a.pickNumber ? Number(a.pickNumber) : Infinity;
      const bn = b.pickNumber ? Number(b.pickNumber) : Infinity;
      return an - bn;
    });

  const result: { pos: string; players: typeof playerCLVs }[] = [];
  for (const pos of POS_ORDER) {
    if (groups[pos].length) result.push({ pos, players: sortByPick(groups[pos]) });
  }
  if (other.length) result.push({ pos: "—", players: sortByPick(other) });
  return result;
}

function buildByeClashData(
  players: ReturnType<typeof getTeamCLV>["playerCLVs"],
  weekNums: number[] | null | undefined,
  weekSchedules: Record<string, string[]> | null | undefined,
): { byeWeeks: Map<string, number>; clashNames: Set<string> } {
  const byeWeeks = new Map<string, number>();
  const clashNames = new Set<string>();
  if (!weekNums || !weekSchedules) return { byeWeeks, clashNames };

  for (const p of players) {
    const pName = (p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).trim();
    const sched = weekSchedules[p.team];
    if (!sched) continue;
    const byeIdx = sched.findIndex((e) => e.toUpperCase() === "BYE");
    if (byeIdx >= 0 && byeIdx < weekNums.length) byeWeeks.set(pName, weekNums[byeIdx]);
  }

  // Flag both players when 2+ at the same position share a bye week
  const posGroups: Record<string, Array<{ name: string; bye: number }>> = {};
  for (const p of players) {
    const pName = (p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).trim();
    const bye = byeWeeks.get(pName);
    if (!bye) continue;
    if (!posGroups[p.position]) posGroups[p.position] = [];
    posGroups[p.position].push({ name: pName, bye });
  }
  for (const group of Object.values(posGroups)) {
    const byeToNames = new Map<number, string[]>();
    for (const { name, bye } of group) {
      if (!byeToNames.has(bye)) byeToNames.set(bye, []);
      byeToNames.get(bye)!.push(name);
    }
    for (const names of byeToNames.values()) {
      if (names.length >= 2) names.forEach((n) => clashNames.add(n));
    }
  }

  return { byeWeeks, clashNames };
}

function RosterTable({ teamCLV, draftAdpLookup, projectionLookup, weekNums, weekSchedules }: {
  teamCLV: ReturnType<typeof getTeamCLV>;
  draftAdpLookup: Map<string, number> | null;
  projectionLookup: Map<string, number> | null;
  weekNums?: number[] | null;
  weekSchedules?: Record<string, string[]> | null;
}) {
  const sc = useStatusColors();
  const grouped = useMemo(() => groupRosterPlayersByPosition(teamCLV.playerCLVs), [teamCLV.playerCLVs]);
  const { byeWeeks, clashNames } = useMemo(
    () => buildByeClashData(teamCLV.playerCLVs, weekNums, weekSchedules),
    [teamCLV.playerCLVs, weekNums, weekSchedules],
  );

  return (
    <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
      <Box component="thead">
        <Box component="tr">
          {["Pos", "Player", "Bye", "Pick #", "Draft ADP", "Current ADP", "Draft CLV", "Current CLV", "Proj"].map(h => (
            <Box component="th" key={h} sx={{
              textAlign: "left", pb: 1, pr: 2,
              color: "text.secondary", fontWeight: 600, fontSize: 12,
              position: "sticky", top: 0, bgcolor: "background.paper",
              borderBottom: "1px solid", borderBottomColor: "divider",
            }}>{h}</Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {grouped.map(({ pos, players }) => (
          <React.Fragment key={pos}>
            <Box component="tr">
              <Box component="td" colSpan={9} sx={{
                pt: 1.5, pb: 0.5, fontSize: 11, fontWeight: 700,
                color: "text.disabled", letterSpacing: 0.5, textTransform: "uppercase",
              }}>{pos}</Box>
            </Box>
            {players.map((p, idx) => {
              const pName = (p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).trim();
              const draftAdp = draftAdpLookup?.get(pName) ?? null;
              const proj = projectionLookup?.get(normalizePlayerName(pName)) ?? null;
              const myPick = p.pickNumber ? Number(p.pickNumber) : NaN;
              const draftClv = (typeof draftAdp === "number" && !isNaN(myPick)) ? myPick - draftAdp : null;
              const byeWeek = byeWeeks.get(pName) ?? null;
              const isClash = clashNames.has(pName);
              return (
                <Box component="tr" key={idx}
                  sx={{ "&:hover > td": { bgcolor: "action.hover" } }}>
                  <Box component="td" sx={{ pr: 2, py: 0.5 }}>
                    <PositionChip position={p.position} size="sm" />
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontWeight: 500, whiteSpace: "nowrap" }}>
                    {pName}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                    color: isClash ? sc.bad : byeWeek ? "text.secondary" : "text.disabled" }}>
                    {byeWeek ? `Wk ${byeWeek}` : "—"}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, color: "text.secondary",
                    fontVariantNumeric: "tabular-nums" }}>
                    {p.pickNumber ? `#${p.pickNumber}` : "—"}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontVariantNumeric: "tabular-nums" }}>
                    {typeof draftAdp === "number" ? draftAdp.toFixed(1) : "—"}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontVariantNumeric: "tabular-nums" }}>
                    {typeof p.udAdp === "number" ? p.udAdp.toFixed(1) : "—"}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                    color: typeof draftClv === "number"
                      ? (draftClv > 0 ? sc.good : draftClv < 0 ? sc.bad : "text.primary")
                      : "text.disabled" }}>
                    {typeof draftClv === "number" ? (draftClv > 0 ? "+" : "") + draftClv.toFixed(1) : "—"}
                  </Box>
                  <Box component="td" sx={{ pr: 2, py: 0.5, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                    color: typeof p.clv === "number"
                      ? (p.clv > 0 ? sc.good : p.clv < 0 ? sc.bad : "text.primary")
                      : "text.disabled" }}>
                    {typeof p.clv === "number" ? (p.clv > 0 ? "+" : "") + p.clv.toFixed(1) : "—"}
                  </Box>
                  <Box component="td" sx={{ py: 0.5, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                    color: typeof proj === "number" ? "text.primary" : "text.disabled" }}>
                    {typeof proj === "number" ? proj.toFixed(1) : "—"}
                  </Box>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function MobileRosterList({ teamCLV, draftAdpLookup, projectionLookup, weekNums, weekSchedules }: {
  teamCLV: ReturnType<typeof getTeamCLV>;
  draftAdpLookup: Map<string, number> | null;
  projectionLookup: Map<string, number> | null;
  weekNums?: number[] | null;
  weekSchedules?: Record<string, string[]> | null;
}) {
  const sc = useStatusColors();
  const grouped = useMemo(() => groupRosterPlayersByPosition(teamCLV.playerCLVs), [teamCLV.playerCLVs]);
  const { byeWeeks, clashNames } = useMemo(
    () => buildByeClashData(teamCLV.playerCLVs, weekNums, weekSchedules),
    [teamCLV.playerCLVs, weekNums, weekSchedules],
  );

  return (
    <Stack spacing={1.25}>
      {grouped.map(({ pos, players }) => (
        <Box key={pos}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", letterSpacing: 0.5, textTransform: "uppercase", mb: 0.75 }}>
            {pos}
          </Typography>
          <Stack spacing={1}>
            {players.map((p, idx) => {
              const pName = (p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).trim();
              const draftAdp = draftAdpLookup?.get(pName) ?? null;
              const proj = projectionLookup?.get(normalizePlayerName(pName)) ?? null;
              const myPick = p.pickNumber ? Number(p.pickNumber) : NaN;
              const draftClv = (typeof draftAdp === "number" && !isNaN(myPick)) ? myPick - draftAdp : null;
              const currentClv = typeof p.clv === "number" ? p.clv : null;

              const byeWeek = byeWeeks.get(pName) ?? null;
              const isClash = clashNames.has(pName);
              return (
                <Paper key={`${pName}-${idx}`} variant="outlined" sx={{ borderRadius: 2, p: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <PositionChip position={p.position || "—"} size="sm" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {pName}
                    </Typography>
                    {byeWeek && (
                      <Tooltip title={isClash ? "Bye week clash at this position" : `Bye week ${byeWeek}`} arrow>
                        <Box sx={{
                          fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 0.75,
                          color: isClash ? sc.bad : "text.secondary",
                          bgcolor: isClash ? "rgba(220,38,38,0.08)" : "action.hover",
                          border: "1px solid",
                          borderColor: isClash ? "rgba(220,38,38,0.3)" : "divider",
                          whiteSpace: "nowrap", flexShrink: 0,
                        }}>
                          Wk {byeWeek}
                        </Box>
                      </Tooltip>
                    )}
                    <Typography sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                      {p.pickNumber ? `#${p.pickNumber}` : "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 0.75 }}>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Draft ADP</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {typeof draftAdp === "number" ? draftAdp.toFixed(1) : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Current ADP</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {typeof p.udAdp === "number" ? p.udAdp.toFixed(1) : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Draft CLV</Typography>
                      <Typography sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: typeof draftClv === "number"
                          ? (draftClv > 0 ? sc.good : draftClv < 0 ? sc.bad : "text.primary")
                          : "text.disabled",
                      }}>
                        {typeof draftClv === "number" ? (draftClv > 0 ? "+" : "") + draftClv.toFixed(1) : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Current CLV</Typography>
                      <Typography sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: typeof currentClv === "number"
                          ? (currentClv > 0 ? sc.good : currentClv < 0 ? sc.bad : "text.primary")
                          : "text.disabled",
                      }}>
                        {typeof currentClv === "number" ? (currentClv > 0 ? "+" : "") + currentClv.toFixed(1) : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Proj</Typography>
                      <Typography sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: typeof proj === "number" ? "text.primary" : "text.disabled",
                      }}>
                        {typeof proj === "number" ? proj.toFixed(1) : "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

// ── Team detail content ───────────────────────────────────────────
function TeamDetailContent({ team, adpLoading, schedule, marketAdpLookup, projectionLookup, adpHistory, isMobile }: {
  team: TeamWithCLV;
  adpLoading: boolean;
  schedule: any;
  marketAdpLookup: Map<string, number>;
  projectionLookup: Map<string, number>;
  adpHistory: PlayerTrend[];
  isMobile: boolean;
}) {
  const sc = useStatusColors();
  const teamCLV = getTeamCLV(team, marketAdpLookup);
  const draftAdpLookup = useMemo(
    () => buildDraftAdpLookup(team.date || "", adpHistory),
    [team.date, adpHistory],
  );

  let formattedDate = "";
  if (team.date) {
    const d = new Date(team.date);
    if (!isNaN(d.getTime())) formattedDate = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  }

  const clv = typeof team.cumulativeCLV === "number" && !isNaN(team.cumulativeCLV) ? team.cumulativeCLV : 0;
  const isPositive = clv >= 0;
  const cumulativeProjection = typeof team.cumulativeProjection === "number" && !isNaN(team.cumulativeProjection) ? team.cumulativeProjection : 0;

  return (
    <Box sx={{ px: 3, py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>
          {team.tournament || "Draft"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {[team.draftSlot ? `Slot ${team.draftSlot}` : null, formattedDate].filter(Boolean).join("  ·  ")}
          {!adpLoading && (
            <Box component="span" sx={{
              ml: 1.5, fontWeight: 700,
              color: isPositive ? sc.good : sc.bad,
            }}>
              {"  ·  "}Cumulative CLV: {isPositive ? "+" : ""}{clv.toFixed(1)}
            </Box>
          )}
          {!adpLoading && (
            <Box component="span" sx={{ ml: 1.5, fontWeight: 700, color: "text.primary" }}>
              {"  ·  "}Cumulative Proj: {cumulativeProjection.toFixed(1)}
            </Box>
          )}
        </Typography>
      </Box>

      {/* Roster and Playoff Stacks */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        {/* Roster section */}
        <Box sx={{ flex: 1, minWidth: 0, pr: { md: 4 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>Roster</Typography>
          <Box>
            {adpLoading ? (
              <Skeleton height={200} />
            ) : isMobile ? (
              <MobileRosterList teamCLV={teamCLV} draftAdpLookup={draftAdpLookup} projectionLookup={projectionLookup} weekNums={schedule?.weekNums} weekSchedules={schedule?.weekSchedules} />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <RosterTable teamCLV={teamCLV} draftAdpLookup={draftAdpLookup} projectionLookup={projectionLookup} weekNums={schedule?.weekNums} weekSchedules={schedule?.weekSchedules} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Playoff Stacks */}
        <Box sx={{
          flex: 1, minWidth: 0,
          pl: { md: 4 },
          borderLeft: { md: "1px solid" },
          borderLeftColor: { md: "divider" },
          mt: { xs: 4, md: 0 },
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>Playoff Stacks</Typography>
          <WeekStacksSidebar
            teamPlayers={teamCLV.playerCLVs}
            TEAM_COLORS={TEAM_COLORS}
            weekNums={schedule?.weekNums}
            weekSchedules={schedule?.weekSchedules}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ── Team detail panel (right) ─────────────────────────────────────
function TeamDetailPanel({ team, adpLoading, schedule, marketAdpLookup, projectionLookup, adpHistory, isMobile }: {
  team: TeamWithCLV | null;
  adpLoading: boolean;
  schedule: any;
  marketAdpLookup: Map<string, number>;
  projectionLookup: Map<string, number>;
  adpHistory: PlayerTrend[];
  isMobile: boolean;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
      {team == null ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", height: "100%", color: "text.disabled", gap: 1, py: 10 }}>
          <Typography sx={{ fontSize: 14 }}>← Select a team to view details</Typography>
        </Box>
      ) : (
        <TeamDetailContent
          team={team}
          adpLoading={adpLoading}
          schedule={schedule}
          marketAdpLookup={marketAdpLookup}
          projectionLookup={projectionLookup}
          adpHistory={adpHistory}
          isMobile={isMobile}
        />
      )}
    </Box>
  );
}

// ── Mobile detail drawer ──────────────────────────────────────────
function TeamDetailDrawer({ open, team, onClose, adpLoading, schedule, marketAdpLookup, projectionLookup, adpHistory, isMobile }: {
  open: boolean;
  team: TeamWithCLV | null;
  onClose: () => void;
  adpLoading: boolean;
  schedule: any;
  marketAdpLookup: Map<string, number>;
  projectionLookup: Map<string, number>;
  adpHistory: PlayerTrend[];
  isMobile: boolean;
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "100%", maxWidth: "100vw" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 2, py: 1.5, borderBottom: "1px solid", borderBottomColor: "divider",
          position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>
          <IconButton size="small" onClick={onClose} aria-label="Back to list">
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {team?.tournament || "Team Detail"}
          </Typography>
        </Box>
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {team && (
            <TeamDetailContent
              team={team}
              adpLoading={adpLoading}
              schedule={schedule}
              marketAdpLookup={marketAdpLookup}
              projectionLookup={projectionLookup}
              adpHistory={adpHistory}
              isMobile={isMobile}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default function MyTeamsPage() {
  const { filteredRawData, rawData, portfolioStacks, portfolioArchetypes, tournamentGroups, hasTeamData, hasMultipleProviders } = useDraftData();
  const { udProjectionLookup, adpData, adpHistory, schedule, adpLoading } = useAdp();
  const { lookupForProvider } = useMarketAdp();
  const sc = useStatusColors();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const hasData = rawData.length > 0;

  const [teamSortCol, setTeamSortCol] = useState<TeamSortCol>("date");
  const [teamSortDir, setTeamSortDir] = useState<SortDirection>("desc");
  // Deep link: /teams?players=Name1,Name2 pre-seeds the combo filter (used by
  // the Insights stack report's "view builds" links).
  const [searchParams, setSearchParams] = useSearchParams();
  const [comboPlayerInputs, setComboPlayerInputs] = useState<AdpPlayerOption[]>(() => {
    const raw = searchParams.get("players");
    if (!raw) return [];
    return raw.split(",").map(s => s.trim()).filter(Boolean)
      .map(name => ({ label: name, value: name, adp: 0 }));
  });
  useEffect(() => {
    if (searchParams.has("players")) setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tournamentFilterInputs, setTournamentFilterInputs] = useState<string[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const adpPlayerOptions = useMemo<AdpPlayerOption[]>(() => {
    if (!adpData.length) return [];
    return adpData
      .map(row => ({
        label: (row.name || "").trim(),
        value: (row.name || "").trim(),
        team: row.team,
        position: row.position,
        adp: row.adp,
      }))
      .sort((a, b) => a.adp - b.adp);
  }, [adpData]);

  const allTeams = useMemo(() => getTeamsTableFromCsv(filteredRawData), [filteredRawData]);

  const tournamentOptions = useMemo(() => {
    const titles = Array.from(new Set(allTeams.map(team => team.tournament || "Draft")));
    const order: Record<string, number> = { pre: 0, regular: 1, undated: 2 };
    // Order by group (pre → regular → undated) then alphabetically, so the
    // Autocomplete's groupBy renders clean, contiguous group headers.
    return titles.sort((a, b) => {
      const ga = order[tournamentGroups.byTitle.get(a) ?? "undated"];
      const gb = order[tournamentGroups.byTitle.get(b) ?? "undated"];
      return ga - gb || a.localeCompare(b);
    });
  }, [allTeams, tournamentGroups]);

  // Group the dropdown only when more than one group is actually present.
  const tournamentGroupBy = useMemo(() => {
    const present = new Set(tournamentOptions.map(o => tournamentGroups.byTitle.get(o) ?? "undated"));
    if (present.size < 2) return undefined;
    return (option: string) => TOURNAMENT_GROUP_LABELS[tournamentGroups.byTitle.get(option) ?? "undated"];
  }, [tournamentOptions, tournamentGroups]);

  const filteredTeams = useMemo(() => {
    return allTeams.filter(team => {
      if (comboPlayerInputs.length > 0) {
        const teamPlayerNames = new Set(team.allPlayerNames);
        const includesAllSelectedPlayers = comboPlayerInputs.every(selected => teamPlayerNames.has(selected.value));
        if (!includesAllSelectedPlayers) return false;
      }

      if (tournamentFilterInputs.length > 0) {
        const teamTournament = team.tournament || "Draft";
        if (!tournamentFilterInputs.includes(teamTournament)) return false;
      }

      return true;
    });
  }, [allTeams, comboPlayerInputs, tournamentFilterInputs]);

  const teamsWithCLV = useMemo((): TeamWithCLV[] => {
    return filteredTeams.map(team => ({
      ...team,
      cumulativeCLV: getTeamCLV(team, lookupForProvider(team.provider)).totalCLV,
      // Projection is always the live value (not snapshot-dependent, no history).
      cumulativeProjection: getTeamProjection(team, udProjectionLookup),
    }));
  }, [filteredTeams, lookupForProvider, udProjectionLookup]);

  const sortedTeams = useMemo((): TeamWithCLV[] => {
    return [...teamsWithCLV].sort((a, b) => {
      if (teamSortCol === "date") {
        const aDate = a.date ? new Date(a.date).getTime() : 0;
        const bDate = b.date ? new Date(b.date).getTime() : 0;
        return teamSortDir === "asc" ? aDate - bDate : bDate - aDate;
      }
      if (teamSortCol === "draftSlot" || teamSortCol === "cumulativeCLV" || teamSortCol === "cumulativeProjection") {
        const aNum = isNaN(Number(a[teamSortCol])) ? -Infinity : Number(a[teamSortCol]);
        const bNum = isNaN(Number(b[teamSortCol])) ? -Infinity : Number(b[teamSortCol]);
        return teamSortDir === "asc" ? aNum - bNum : bNum - aNum;
      }
      const aStr = String(a[teamSortCol] ?? "");
      const bStr = String(b[teamSortCol] ?? "");
      return teamSortDir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [teamsWithCLV, teamSortCol, teamSortDir]);

  const maxAbsCLV = useMemo(() =>
    Math.max(...sortedTeams.map(t => Math.abs(t.cumulativeCLV || 0)), 1),
    [sortedTeams]);

  const maxProjection = useMemo(() =>
    Math.max(...sortedTeams.map(t => t.cumulativeProjection || 0), 1),
    [sortedTeams]);

  const selectedTeam = useMemo(() =>
    sortedTeams.find(t => t.draftId === selectedTeamId) ?? null,
    [sortedTeams, selectedTeamId]);

  // Summary stats
  const avgCLV = useMemo(() => {
    if (!sortedTeams.length) return null;
    const total = sortedTeams.reduce((s, t) => s + (t.cumulativeCLV || 0), 0);
    return total / sortedTeams.length;
  }, [sortedTeams]);
  const bestCLV = useMemo(() =>
    sortedTeams.length ? Math.max(...sortedTeams.map(t => t.cumulativeCLV || 0)) : null,
    [sortedTeams]);
  const worstCLV = useMemo(() =>
    sortedTeams.length ? Math.min(...sortedTeams.map(t => t.cumulativeCLV || 0)) : null,
    [sortedTeams]);
  const avgProjection = useMemo(() => {
    if (!sortedTeams.length) return null;
    const total = sortedTeams.reduce((s, t) => s + (t.cumulativeProjection || 0), 0);
    return total / sortedTeams.length;
  }, [sortedTeams]);

  // Per-platform CLV/projection summary. A blended "Avg CLV" across UD and DK is
  // an average of two different markets — it can hide crushing one board while
  // bleeding on the other, which is exactly the comparison this page is for.
  const platformStats = useMemo(() => teamPlatformStats(sortedTeams), [sortedTeams]);
  const splitKpis = hasMultipleProviders && platformStats.length > 1;

  const archetypeByDraftId = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    if (portfolioArchetypes) {
      for (const r of portfolioArchetypes.teamResults) {
        m.set(r.draftId, r.summary);
      }
    }
    return m;
  }, [portfolioArchetypes]);

  const multiStackRate = useMemo(() => {
    if (!portfolioStacks || !portfolioStacks.totalTeams) return null;
    const count = portfolioStacks.teamResults.filter(r => r.gameStackCount >= 2).length;
    return count / portfolioStacks.totalTeams;
  }, [portfolioStacks]);

  const playoffBringBackRate = useMemo(() => {
    if (!schedule?.weekNums || !schedule?.weekSchedules) return null;
    const teams = getTeamsTableFromCsv(filteredRawData);
    if (teams.length === 0) return null;
    const teamsWithBringBack = teams.filter(team => {
      const result = detectTeamStacks(team, schedule);
      return result.bringBackCount > 0;
    }).length;
    return teamsWithBringBack / teams.length;
  }, [filteredRawData, schedule]);

  const playoffDomeStackRate = useMemo(() => {
    if (!schedule?.weekNums || !schedule?.weekSchedules) return null;
    const teams = getTeamsTableFromCsv(filteredRawData);
    if (teams.length === 0) return null;
    const teamsWithWeek17Dome = teams.filter(team => {
      const result = detectTeamStacks(team, schedule);
      return result.domeWeeks.includes(17);
    }).length;
    return teamsWithWeek17Dome / teams.length;
  }, [filteredRawData, schedule]);


  const handleSort = (col: TeamSortCol) => {
    if (col === teamSortCol) setTeamSortDir(d => d === "asc" ? "desc" : "asc");
    else { setTeamSortCol(col); setTeamSortDir("desc"); }
  };

  const handleSelectTeam = (id: string) => {
    setSelectedTeamId(id);
    if (isMobile) setDrawerOpen(true);
  };

  if (!hasData) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, color: "text.disabled", gap: 2 }}>
        <GroupsOutlinedIcon sx={{ fontSize: 72, opacity: 0.3 }} />
        <Typography variant="h6">No draft teams yet</Typography>
        <Typography variant="body2">Upload your Underdog CSV to see your teams →</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: "flex", flexDirection: "column", minHeight: "100%" }}>

      {/* ── Page header ──────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 2, mb: 2, flexShrink: 0 }}>
        <Typography variant="h2" color="text.primary" sx={{ fontWeight: 600 }}>
          My Teams <Chip label={`${sortedTeams.length}`} size="small"
            sx={{ ml: 1, bgcolor: "primary.main", color: "#fff", fontWeight: 700 }} />
        </Typography>
        {/* Player filter at page level */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Autocomplete
            multiple
            options={adpPlayerOptions}
            disableCloseOnSelect
            filterSelectedOptions
            value={comboPlayerInputs}
            onChange={(_, val) => setComboPlayerInputs(val as AdpPlayerOption[])}
            getOptionLabel={o => o.label}
            renderInput={params => (
              <TextField {...params} size="small" label="Filter by player"
                sx={{ minWidth: 220, maxWidth: 340 }} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option.label} {...tagProps} size="small" />;
              })
            }
            isOptionEqualToValue={(o, v) => o.value === v.value}
          />
          <Autocomplete
            multiple
            options={tournamentOptions}
            groupBy={tournamentGroupBy}
            disableCloseOnSelect
            filterSelectedOptions
            value={tournamentFilterInputs}
            onChange={(_, val) => setTournamentFilterInputs(val)}
            renderInput={params => (
              <TextField {...params} size="small" label="Filter by tournament"
                sx={{ minWidth: 220, maxWidth: 340 }} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option} {...tagProps} size="small" />;
              })
            }
          />
          {(comboPlayerInputs.length > 0 || tournamentFilterInputs.length > 0) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setComboPlayerInputs([]);
                setTournamentFilterInputs([]);
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Summary stats strip ──────────────────────────────────── */}
      {!adpLoading && sortedTeams.length > 0 && (
        <Box
          sx={{
            mb: 3,
            flexShrink: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              xl: "repeat(5, minmax(0, 1fr))",
            },
            gap: { xs: 1, md: 1.5 },
          }}
        >
          {([
            { label: "Drafts", value: sortedTeams.length, perPlatform: (p: typeof platformStats[number]) => String(p.drafts) },
            { label: "Avg CLV", value: avgCLV !== null ? (avgCLV > 0 ? "+" : "") + avgCLV.toFixed(1) : "\u2014", color: avgCLV !== null ? (avgCLV >= 0 ? sc.good : sc.bad) : undefined, perPlatform: (p: typeof platformStats[number]) => fmtSigned(p.avgClv), tone: (p: typeof platformStats[number]) => p.avgClv === null ? undefined : (p.avgClv >= 0 ? sc.good : sc.bad) },
            { label: "Avg Proj", value: avgProjection !== null ? avgProjection.toFixed(1) : "\u2014", perPlatform: (p: typeof platformStats[number]) => p.avgProjection !== null ? p.avgProjection.toFixed(1) : "\u2014" },
            { label: "Best", value: bestCLV !== null ? (bestCLV > 0 ? "+" : "") + bestCLV.toFixed(1) : "\u2014", color: sc.good, perPlatform: (p: typeof platformStats[number]) => fmtSigned(p.bestClv), tone: () => sc.good },
            { label: "Worst", value: worstCLV !== null ? (worstCLV > 0 ? "+" : "") + worstCLV.toFixed(1) : "\u2014", color: sc.bad, perPlatform: (p: typeof platformStats[number]) => fmtSigned(p.worstClv), tone: () => sc.bad },
          ] as KpiTile[]).map(({ label, value, color, perPlatform, tone }) => (
            <Box key={label} role="group" aria-label={label} sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Typography>
              {splitKpis ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.375 }}>
                  {platformStats.map(p => (
                    <Box key={p.provider} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <PlatformChip provider={p.provider} size="sm" variant="outlined" />
                      <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                        color: tone?.(p) ?? color ?? "text.primary" }}>
                        {perPlatform(p)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: color ?? "text.primary" }}>{value}</Typography>
              )}
            </Box>
          ))}

          {portfolioArchetypes?.distribution[0] && (
            <Tooltip title="Your most common roster construction style based on draft capital allocation." arrow>
              <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Top Build</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                  {(portfolioArchetypes.distribution[0].rate * 100).toFixed(0)}% {portfolioArchetypes.distribution[0].label}
                </Typography>
              </Box>
            </Tooltip>
          )}

          {portfolioStacks && (
            <>
              <Tooltip title="Teams with a QB paired with a WR or TE from the same NFL team." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Game Stack</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>{(portfolioStacks.gameStackRate * 100).toFixed(0)}%</Typography>
                </Box>
              </Tooltip>
              <Tooltip title="Teams with two separate QB stacks (QB + same-team WR/TE/RB) from different NFL teams." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Multi-Stack</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>{multiStackRate !== null ? (multiStackRate * 100).toFixed(0) + "%" : "\u2014"}</Typography>
                </Box>
              </Tooltip>
              {portfolioStacks.orphanQBRate > 0 && (
                <Tooltip title="Teams where your QB has no WR, TE, or RB from the same NFL team." arrow>
                  <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Orphan QB Teams</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>
                      {(portfolioStacks.orphanQBRate * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </>
          )}

          <Box sx={{ display: { xs: "none", md: hasTeamData ? "contents" : "none" } }}>
            {playoffDomeStackRate !== null ? (
              <Tooltip title="Teams with a QB stack plus an opposing-team pass-catcher or RB (bring-back) in a dome stadium in week 17 (championship week)." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Wk 17 Dome Bring Back</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>{(playoffDomeStackRate * 100).toFixed(0)}%</Typography>
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title="Teams with a QB stack plus an opposing-team pass-catcher or RB (bring-back) in a dome stadium in week 17 (championship week). Requires NFL schedule data." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.disabled", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Wk 17 Dome Bring Back</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                    <Typography sx={{ fontSize: 13, color: "text.disabled", fontWeight: 600 }}>Loading...</Typography>
                  </Box>
                </Box>
              </Tooltip>
            )}
            {playoffBringBackRate !== null ? (
              <Tooltip title="Teams with a QB stack plus a pass-catcher or RB from the opposing team in playoff weeks (15-17)." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Playoff Bring-Back</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>{(playoffBringBackRate * 100).toFixed(0)}%</Typography>
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title="Teams with a QB stack plus a pass-catcher or RB from the opposing team in playoff weeks (15-17). Requires NFL schedule data." arrow>
                <Box sx={{ p: { xs: 1, md: 1.25 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5, cursor: "default" }}>
                  <Typography sx={{ fontSize: 11, color: "text.disabled", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>Playoff Bring-Back</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                    <Typography sx={{ fontSize: 13, color: "text.disabled", fontWeight: 600 }}>Loading...</Typography>
                  </Box>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {/* ── Value curves — how each build banked its edge ─────────── */}
      <EntryValueCurves teams={teamsWithCLV} lookupForProvider={lookupForProvider} />

      {/* ── Master–Detail panel — grows to fill remaining height ──── */}
      <Paper variant="outlined" sx={{ display: { xs: "block", md: "flex" }, borderRadius: 2,
        overflow: "hidden",
        height: { xs: MOBILE_TEAM_LIST_MAX_HEIGHT, md: "auto" },
        flex: { md: 1 }, minHeight: { md: 480 } }}>
        <TeamListPanel
          teams={sortedTeams}
          maxAbsCLV={maxAbsCLV}
          maxProjection={maxProjection}
          selectedId={selectedTeamId}
          onSelect={handleSelectTeam}
          adpLoading={adpLoading}
          sortCol={teamSortCol}
          sortDir={teamSortDir}
          onSort={handleSort}
          archetypeByDraftId={archetypeByDraftId}
          showPlatform={hasMultipleProviders}
        />
        {/* Right detail — only rendered on md+ */}
        <Box sx={{ display: { xs: "none", md: "flex" }, flex: 1, minWidth: 0,
          borderLeft: "1px solid", borderLeftColor: "divider", overflowY: "auto" }}>
          <TeamDetailPanel
            team={selectedTeam}
            adpLoading={adpLoading}
            schedule={schedule}
            marketAdpLookup={lookupForProvider(selectedTeam?.provider)}
            projectionLookup={udProjectionLookup}
            adpHistory={adpHistory}
            isMobile={isMobile}
          />
        </Box>
      </Paper>

      {/* Mobile drawer */}
      <TeamDetailDrawer
        open={drawerOpen}
        team={selectedTeam}
        onClose={() => setDrawerOpen(false)}
        adpLoading={adpLoading}
        schedule={schedule}
        marketAdpLookup={lookupForProvider(selectedTeam?.provider)}
        projectionLookup={udProjectionLookup}
        adpHistory={adpHistory}
        isMobile={isMobile}
      />

    </Box>
  );
}
